'use client';

import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';

import { cn } from '@/hen-ui/utils/index';

const DEFAULT_MAX = 100;

/**
 * Resolves the authored value to what both the accessible primitive and the
 * indicator render: numbers (and numeric strings from data bindings) are
 * clamped to `[0, max]`, including `±Infinity`; absent, `NaN` or non-numeric
 * values return `null`, which Radix renders as an indeterminate bar with no
 * `aria-valuenow`.
 */
function normalizeProgressValue(value: unknown, max: number): number | null {
  let numeric: number;
  if (typeof value === 'number') {
    numeric = value;
  } else if (typeof value === 'string' && value.trim() !== '') {
    numeric = Number(value);
  } else {
    return null;
  }
  if (Number.isNaN(numeric)) return null;
  return Math.min(Math.max(numeric, 0), max);
}

function normalizeProgressMax(max: unknown): number {
  return typeof max === 'number' && Number.isFinite(max) && max > 0
    ? max
    : DEFAULT_MAX;
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>
>(({ className, value, max, ...props }, ref) => {
  const resolvedMax = normalizeProgressMax(max);
  const resolvedValue = normalizeProgressValue(value, resolvedMax);
  const percent =
    resolvedValue === null ? 0 : (resolvedValue / resolvedMax) * 100;

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        'relative h-4 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800',
        className,
      )}
      value={resolvedValue}
      max={resolvedMax}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className='h-full w-full flex-1 bg-neutral-900 transition-all dark:bg-neutral-50'
        style={{ transform: `translateX(-${100 - percent}%)` }}
      />
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
